import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import StreakBanner from '../components/StreakBanner';
import axios from 'axios';
import {
  Trophy, Zap, Gift, Target, Flame, BookOpen, Crown, ArrowRight, Activity,
  GraduationCap, TrendingUp, Star, ArrowUpRight, Sparkles, Swords,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECT_COLORS = {
  Physics: '#A78BFA',
  Chemistry: '#34D399',
  Biology: '#38BDF8',
  Mathematics: '#F59E0B',
  'General Aptitude': '#F472B6',
};

const greetingForHour = (h) => {
  if (h >= 4 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Burning the midnight oil';
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="px-3 py-2 rounded-md border shadow-lg text-xs"
      style={{ background: 'rgb(var(--bg-elevated))', borderColor: 'rgb(var(--border))' }}
    >
      <p className="text-fg-muted">{label}</p>
      <p className="font-semibold text-fg">{payload[0].value}%</p>
    </div>
  );
};

const StatCard = ({ label, value, delta, deltaLabel, testId }) => (
  <div
    className="rounded-lg border p-4 sm:p-5 transition hover:border-app-strong motion-fade-up"
    style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
    data-testid={testId}
  >
    <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">{label}</p>
    <div className="flex items-baseline gap-2">
      <span className="text-3xl font-bold text-fg tracking-tight">{value}</span>
      {delta != null && (
        <span
          className="text-[11px] font-semibold flex items-center gap-0.5"
          style={{ color: delta >= 0 ? 'rgb(var(--success))' : 'rgb(var(--accent))' }}
        >
          {delta >= 0 ? '+' : ''}{delta}
          <ArrowUpRight className="w-3 h-3" style={{ transform: delta < 0 ? 'rotate(90deg)' : 'none' }} />
        </span>
      )}
    </div>
    {deltaLabel && (
      <p className="text-[11px] text-fg-faint mt-1">{deltaLabel}</p>
    )}
  </div>
);

const QuickAction = ({ icon: Icon, label, sub, onClick, testId, accent }) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition hover:border-app-strong hover:bg-card-hover"
    style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
    data-testid={testId}
  >
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center transition group-hover:scale-105"
      style={{ background: accent ? 'rgba(239, 68, 68, 0.12)' : 'rgba(255,255,255,0.04)' }}
    >
      <Icon className="w-4 h-4" style={{ color: accent ? 'rgb(var(--accent))' : 'rgb(var(--fg-secondary))' }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-fg truncate">{label}</p>
      {sub && <p className="text-[11px] text-fg-muted truncate">{sub}</p>}
    </div>
    <ArrowRight className="w-3.5 h-3.5 text-fg-faint group-hover:text-accent group-hover:translate-x-0.5 transition" />
  </button>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [poolStats, setPoolStats] = useState({ totalPools: 0, activePools: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [subStatus, setSubStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [poolsRes, analyticsRes, statsRes, liveRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/quiz-pools`),
        axios.get(`${API_URL}/api/profile/analytics`),
        axios.get(`${API_URL}/api/profile/stats`),
        axios.get(`${API_URL}/api/stats/live`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/subscriptions/status`).catch(() => ({ data: null })),
      ]);
      setPoolStats({
        totalPools: poolsRes.data.length,
        activePools: poolsRes.data.filter(p => p.current_participants < p.max_participants).length,
      });
      setAnalytics(analyticsRes.data);
      setProfileStats(statsRes.data);
      setLiveStats(liveRes.data);
      setSubStatus(subRes.data);
    } catch { /* silent */ }
  }, []);

  const [greeting] = useState(() => greetingForHour(new Date().getHours()));
  useEffect(() => { fetchData(); }, [fetchData]);

  const isPro = user?.subscription_status === 'active' || user?.role === 'admin';
  const firstName = (user?.name || '').split(' ')[0] || 'Scholar';
  const superPoints = user?.super_points || 0;
  const superPointsINR = Math.round(superPoints * 0.5);
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Header />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Executive Brief — hero */}
        <section
          className="relative overflow-hidden rounded-xl border mb-6 motion-fade-up"
          style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
          data-testid="dashboard-hero"
        >
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{
            background: 'radial-gradient(circle at 90% 0%, rgba(239,68,68,0.18), transparent 45%)',
          }} />
          <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-end">
            <div>
              <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
                <Sparkles className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
                <span>Executive Brief · {today}</span>
                {liveStats?.active_now && (
                  <span className="inline-flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full text-[10px]"
                    style={{ background: 'rgba(16,185,129,0.12)', color: 'rgb(16,185,129)' }}>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                    </span>
                    {liveStats.active_now.toLocaleString('en-IN')} learners online
                  </span>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-fg tracking-tight mb-2" data-testid="welcome-message">
                {greeting}, <span style={{ color: 'rgb(var(--accent))' }}>{firstName}</span>.
              </h1>
              <p className="text-sm sm:text-base text-fg-muted max-w-2xl leading-relaxed">
                {(user?.points || 0).toLocaleString('en-IN')} points · {analytics?.streak || 0}-day streak · {poolStats.activePools} live pools. Ready to climb the ranks?
              </p>

              <div className="flex flex-wrap gap-2 mt-5">
                <button
                  onClick={() => navigate('/quiz-pools')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
                  style={{ background: 'rgb(var(--accent))' }}
                  data-testid="hero-cta-pools"
                >
                  <Trophy className="w-4 h-4" /> Join Quiz Pool
                </button>
                <button
                  onClick={() => navigate('/h2h')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border text-fg transition hover:bg-card-hover"
                  style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                  data-testid="hero-cta-h2h"
                >
                  <Swords className="w-4 h-4" /> 1v1 Battle
                </button>
                <button
                  onClick={() => navigate('/daily-mocks')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border text-fg transition hover:bg-card-hover"
                  style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                  data-testid="hero-cta-mocks"
                >
                  <Activity className="w-4 h-4" /> Daily Mock
                </button>
              </div>
            </div>

            {/* Hero-right pill status */}
            <div className="flex flex-col gap-2 items-start lg:items-end shrink-0">
              {isPro ? (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest"
                  style={{ background: 'linear-gradient(135deg,#F59E0B,#EAB308)', color: '#111' }}
                >
                  <Crown className="w-3 h-3" /> Pro Member
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-widest border"
                  style={{ borderColor: 'rgb(var(--border))', color: 'rgb(var(--fg-muted))' }}
                >
                  Free Plan
                </span>
              )}
              <div className="text-xs text-fg-faint text-right">v1.0 · Aptivon OS</div>
            </div>
          </div>
        </section>

        {/* Subscription health banners */}
        {subStatus?.payment_issue && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border"
            style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.35)' }}
            data-testid="payment-issue-banner">
            <div className="flex-1">
              <p className="font-semibold text-sm" style={{ color: 'rgb(var(--accent))' }}>Payment issue on your Pro subscription</p>
              <p className="text-xs text-fg-muted">Last renewal failed. Update payment to keep unlimited AI quizzes.</p>
            </div>
            <button onClick={() => navigate('/upgrade')}
              className="shrink-0 px-4 py-2 rounded-md text-white text-sm font-semibold"
              style={{ background: 'rgb(var(--accent))' }}
              data-testid="fix-payment-btn">Fix payment →</button>
          </div>
        )}
        {!subStatus?.payment_issue && subStatus?.is_trial && subStatus?.trial_days_left != null && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border"
            style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}
            data-testid="trial-countdown-banner">
            <div className="flex-1">
              <p className="font-semibold text-sm text-fg">Pro trial: {subStatus.trial_days_left} day{subStatus.trial_days_left !== 1 ? 's' : ''} left</p>
              <p className="text-xs text-fg-muted">Keep unlimited AI quizzes — annual works out to under ₹7/day.</p>
            </div>
            <button onClick={() => navigate('/upgrade')}
              className="shrink-0 px-4 py-2 rounded-md text-sm font-semibold text-white"
              style={{ background: 'rgb(var(--warning))' }}
              data-testid="trial-upgrade-btn">Keep Pro →</button>
          </div>
        )}
        {subStatus?.trial_expired && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border"
            style={{ background: 'rgb(var(--card-hover))', borderColor: 'rgb(var(--border))' }}
            data-testid="trial-expired-banner">
            <div className="flex-1">
              <p className="font-semibold text-sm text-fg">Your Pro trial has ended</p>
              <p className="text-xs text-fg-muted">Back on Free (5 quizzes/day). Rejoin Pro from under ₹7/day.</p>
            </div>
            <button onClick={() => navigate('/upgrade')}
              className="shrink-0 px-4 py-2 rounded-md text-sm font-semibold text-white"
              style={{ background: 'rgb(var(--accent))' }}
              data-testid="trial-expired-upgrade-btn">See plans →</button>
          </div>
        )}

        {/* Streak banner */}
        <div className="mb-6"><StreakBanner /></div>

        {/* Main grid: rewards hero (2 cols) + stat cards (2 cols) on lg */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">

          {/* REWARDS HERO — spans 2 cols */}
          <div
            className="lg:col-span-2 relative overflow-hidden rounded-xl border p-6 motion-fade-up motion-delay-1"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.08) 100%)',
              borderColor: 'rgba(239,68,68,0.35)',
            }}
            data-testid="rewards-hero-card"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none"
              style={{ background: 'rgba(239,68,68,0.18)' }} />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest"
                  style={{ background: 'rgba(239,68,68,0.14)', color: 'rgb(var(--accent))' }}>
                  <Star className="w-3 h-3 fill-current" /> Reward Wallet
                </span>
                <span className="text-[10px] uppercase tracking-widest text-fg-faint">1 ⭐ = ₹0.50</span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <Star className="w-8 h-8 fill-current" style={{ color: 'rgb(var(--accent))' }} />
                <span className="text-5xl font-bold text-fg tracking-tight">{superPoints.toLocaleString('en-IN')}</span>
                <span className="text-sm text-fg-muted ml-1">Super Points</span>
              </div>
              <p className="text-sm text-fg-muted mb-4">
                ≈ <span className="font-semibold text-fg">₹{superPointsINR.toLocaleString('en-IN')}</span> redeemable · Cash out to Amazon, Flipkart, Unacademy & more.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => navigate('/rewards')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
                  style={{ background: 'rgb(var(--accent))' }}
                  data-testid="rewards-hero-redeem-btn"
                >
                  <Gift className="w-4 h-4" /> Redeem now
                </button>
                <button
                  onClick={() => navigate('/pro-leaderboard')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border text-fg transition hover:bg-card-hover"
                  style={{ background: 'transparent', borderColor: 'rgb(var(--border))' }}
                  data-testid="rewards-hero-earn-btn"
                >
                  <Trophy className="w-4 h-4" /> Earn more
                </button>
              </div>
              {superPoints === 0 && (
                <p className="text-[11px] text-fg-faint mt-3">
                  Finish in the top 3% each month to earn up to 15,000 ⭐ (₹7,500)
                </p>
              )}
            </div>
          </div>

          {/* 4 stat cards in a 2x2 mini-grid */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            <StatCard
              label="Points"
              value={(user?.points || 0).toLocaleString('en-IN')}
              deltaLabel="Play to earn more"
              testId="quick-stat-points"
            />
            <StatCard
              label="Quizzes"
              value={profileStats?.total_quizzes || 0}
              delta={profileStats?.total_wins || 0}
              deltaLabel="wins on record"
              testId="quick-stat-quizzes"
            />
            <StatCard
              label="Win Rate"
              value={`${profileStats?.win_rate || 0}%`}
              deltaLabel={`Best rank #${profileStats?.best_rank || '—'}`}
              testId="quick-stat-win-rate"
            />
            <StatCard
              label="Streak"
              value={`${analytics?.streak || 0}d`}
              deltaLabel="Keep it alive"
              testId="quick-stat-streak"
            />
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] uppercase tracking-widest text-fg-faint font-medium">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickAction icon={GraduationCap} label="AI Courses" sub="Video lessons + quizzes" onClick={() => navigate('/courses')} testId="quick-action-courses" accent />
            <QuickAction icon={TrendingUp} label="97+ Report" sub="Percentile & AI plan" onClick={() => navigate('/performance')} testId="quick-action-performance" />
            <QuickAction icon={Trophy} label="Quiz Pools" sub="Compete for points" onClick={() => navigate('/quiz-pools')} testId="quick-action-join-quiz" />
            <QuickAction icon={BookOpen} label="Practice" sub="Free timed quiz" onClick={() => navigate('/practice')} testId="quick-action-practice" />
          </div>
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div
            className="rounded-xl border p-5 motion-fade-up motion-delay-2"
            style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
            data-testid="score-trend-chart"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Score Trend</p>
                <h3 className="text-base font-semibold text-fg mt-0.5">Last quizzes</h3>
              </div>
              <TrendingUp className="w-4 h-4 text-fg-muted" />
            </div>
            {analytics?.score_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={analytics.score_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--fg-faint))' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: 'rgb(var(--fg-faint))' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="score" stroke="rgb(var(--accent))" strokeWidth={2} dot={{ fill: 'rgb(var(--accent))', r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-fg-faint text-sm">
                Play quizzes to see your score trend
              </div>
            )}
          </div>

          <div
            className="rounded-xl border p-5 motion-fade-up motion-delay-3"
            style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
            data-testid="subject-performance-chart"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Subject Performance</p>
                <h3 className="text-base font-semibold text-fg mt-0.5">Average score</h3>
              </div>
              <Target className="w-4 h-4 text-fg-muted" />
            </div>
            {analytics?.subject_performance?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.subject_performance} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: 'rgb(var(--fg-faint))' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="subject" tick={{ fontSize: 10, fill: 'rgb(var(--fg-muted))' }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="avg_score" radius={[0, 4, 4, 0]} barSize={16}>
                    {analytics.subject_performance.map((entry, idx) => (
                      <Cell key={idx} fill={SUBJECT_COLORS[entry.subject] || 'rgb(var(--accent))'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-fg-faint text-sm">
                Play quizzes to see subject performance
              </div>
            )}
          </div>
        </div>

        {/* Activity heatmap */}
        <div
          className="rounded-xl border p-5 mb-6 motion-fade-up motion-delay-4"
          style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
          data-testid="activity-chart"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Activity</p>
              <h3 className="text-base font-semibold text-fg mt-0.5">Last 14 days</h3>
            </div>
            <Flame className="w-4 h-4 text-fg-muted" />
          </div>
          {analytics?.activity ? (
            <div className="flex items-end gap-1.5 justify-between h-24">
              {analytics.activity.map((day, idx) => {
                const maxQ = Math.max(...analytics.activity.map(d => d.quizzes), 1);
                const height = day.quizzes > 0 ? Math.max(20, (day.quizzes / maxQ) * 100) : 8;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: ${day.quizzes} quizzes`}>
                    <div
                      className="w-full rounded-sm transition-all"
                      style={{
                        height: `${height}%`,
                        background: day.quizzes > 0 ? 'rgb(var(--accent))' : 'rgba(255,255,255,0.06)',
                      }}
                    />
                    <span className="text-[10px] text-fg-faint">{day.day}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-fg-faint text-sm">Loading…</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
